import { ISO_21930_ID } from 'components/v2/compiler/constants';
import { NOT_ASSIGNED, TOOL_TYPES } from 'constants/constants';
import React from 'react';
import { VerificationType } from 'services/EpdClient';
import styled from 'styled-components';
import { VerifierType } from 'types/types';
import DebugOnly from 'util/dev-tools/DebugOnly';
import RenderCounter from 'util/dev-tools/RenderCounter';
import { FieldHintText } from 'util/fieldDescriptions';
import { formatCompanyAddress } from 'util/utils';
import usePcrV2 from '../../../../components/v2/compiler/hooks/usePcrV2';
import CheckboxesCellPdf from './components/CheckboxesCellPdf';
import CheckboxGroupPdf from './components/CheckboxGroupPdf';
import { PdfAreaDontBreak, PdfGridsArea, PdfH1, PdfPage, PdfTable, PdfThLabel } from './Styles.styled';
import usePdfData from './utils/usePdfData';

const ProgrammeInformationPage: React.FC = () => {
  const { epdVersionId, epdDefinition, pcrInformation, thirdPartyVerification } = usePdfData();
  const isPcrVersion2 = usePcrV2();
  const isFollowUpInvolvesThirdPartyVerifierYes =
    thirdPartyVerification?.isCreatedByApiFullyIntegratedPreverifiedTool ||
    thirdPartyVerification?.followUpInvolvesThirdPartyVerifier === 'Yes';

  const standards = epdDefinition.statementOfConformityWithStandards?.toString().split(',');
  const supportsIso21930 = standards?.find((x) => x === ISO_21930_ID);
  return (
    <PdfPage>
      <DebugOnly>
        <RenderCounter componentName="ProgrammeInformationPage" threshold={3} />
      </DebugOnly>
      <PdfPageGaps>
        <div>
          {isPcrVersion2 && <PdfH1 style={{ marginBottom: '24px' }}>General Information</PdfH1>}
          <PdfH1Version $isVersionTwo={isPcrVersion2}>Programme information</PdfH1Version>
          <PdfTable>
            <thead>
              <tr>
                <PdfThLabel />
                <th></th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>Programme</td>
                <td>International EPD System</td>
              </tr>
              <tr>
                <td>Address</td>
                <td>
                  EPD International AB
                  <br />
                  Box 210 60
                  <br />
                  SE-100 31 Stockholm
                  <br />
                  Sweden
                </td>
              </tr>
              <tr>
                <td>Website</td>
                <td>www.environdec.com</td>
              </tr>
              <tr>
                <td>E-mail</td>
                <td>support@environdec.com</td>
              </tr>
            </tbody>
          </PdfTable>
        </div>
        <div>
          <PdfH1Version $isVersionTwo={isPcrVersion2}>Product category rules</PdfH1Version>
          <PdfTable>
            <thead>
              <tr>
                <PdfThLabel />
                <th></th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td colSpan={2}>
                  {supportsIso21930
                    ? 'CEN standard EN 15804 and ISO standard ISO 21930 serve as the core Product Category Rules (PCR)'
                    : 'CEN standard EN 15804 serves as the Core Product Category Rules (PCR)'}
                </td>
              </tr>
              <tr>
                <td>Product Category Rules (PCR)</td>
                <td>{pcrInformation.pcr?.fullName}</td>
              </tr>
              <tr>
                <td>PCR review was conducted by</td>
                <td>
                  The Technical Committee of the International EPD System. See www.environdec.com for a list of members.
                  <br />
                  <br />
                  <FieldHintText fieldName="EPDCompiler.PcrInformation.PcrReviewWasConductedBy" />
                </td>
              </tr>
              {pcrInformation.cpcr?.fullName && (
                <>
                  <tr>
                    <td>Complementary Product Category Rules (c-PCR)</td>
                    <td>{pcrInformation.cpcr?.fullName}</td>
                  </tr>
                  <tr>
                    <td>c-PCR review was conducted by</td>
                    <td>The Technical Committee of the International EPD System</td>
                  </tr>
                </>
              )}
            </tbody>
          </PdfTable>
        </div>
        <PdfAreaDontBreak>
          <PdfH1Version $isVersionTwo={isPcrVersion2}>Verification</PdfH1Version>
          <PdfTable>
            <thead>
              <tr>
                <PdfThLabel />
                <th></th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>Independent third-party verification of the declaration and data, according to ISO 14025:2006, via</td>
                <td>
                  <CheckboxesCellPdf>
                    <CheckboxGroupPdf
                      label="EPD verification through an individual EPD verification"
                      checked={thirdPartyVerification?.verificationType === VerificationType.EPDVerification}
                    />
                    <CheckboxGroupPdf
                      label="EPD verification through EPD Process Certification*"
                      checked={thirdPartyVerification?.verificationType === VerificationType.EPDProcessCertification}
                    />

                    {thirdPartyVerification?.isCreatedByApiFullyIntegratedPreverifiedTool ? (
                      <CheckboxGroupPdf
                        label="Fully pre-verified EPD tool"
                        checked={thirdPartyVerification?.isCreatedByApiFullyIntegratedPreverifiedTool}
                      />
                    ) : (
                      <CheckboxGroupPdf
                        label="EPD verification through a fully pre-verified tool"
                        checked={thirdPartyVerification?.verificationType === VerificationType.EPDPreverifiedTool}
                      />
                    )}
                  </CheckboxesCellPdf>
                </td>
              </tr>
              {thirdPartyVerification?.verificationType === VerificationType.EPDVerification &&
                thirdPartyVerification?.epdVerifier && (
                  <>
                    {thirdPartyVerification?.epdVerifier?.verifierType === VerifierType.Individual && (
                      <>
                        <tr>
                          <td>Third-party verifier</td>
                          <td>{thirdPartyVerification?.epdVerifier?.displayName}</td>
                        </tr>
                        <tr>
                          <td>Approved by</td>
                          <td>International EPD System</td>
                        </tr>
                      </>
                    )}
                    {thirdPartyVerification?.epdVerifier?.verifierType === VerifierType.Body && (
                      <>
                        <tr>
                          <td>Third-party verifier</td>
                          <td>{thirdPartyVerification?.epdVerifier?.companyName}</td>
                        </tr>
                        <tr>
                          <td>Accredited by</td>
                          <td>{thirdPartyVerification?.epdVerifier?.information}</td>
                        </tr>
                        <tr>
                          <td>Accredited certification body address</td>
                          <td>{formatCompanyAddress(thirdPartyVerification?.epdVerifier?.companyAddress, '')}</td>
                        </tr>
                      </>
                    )}
                  </>
                )}
              {thirdPartyVerification?.verificationType === VerificationType.EPDProcessCertification &&
                thirdPartyVerification?.processCertificateIssuedBy && (
                  <>
                    <tr>
                      <td>EPD process certificate issued by</td>
                      <td>{thirdPartyVerification?.processCertificateIssuedBy?.homeCompanyName}</td>
                    </tr>
                    <tr>
                      <td>Accredited by</td>
                      <td>{thirdPartyVerification?.processCertificateIssuedBy?.information}</td>
                    </tr>
                    {thirdPartyVerification?.processCertificateIssuedBy?.verifierType === VerifierType.Body && (
                      <tr>
                        <td>Accredited certification body address</td>
                        <td>
                          {formatCompanyAddress(thirdPartyVerification?.processCertificateIssuedBy?.companyAddress, '')}
                        </td>
                      </tr>
                    )}
                  </>
                )}

              {((thirdPartyVerification?.verificationType === VerificationType.EPDPreverifiedTool &&
                thirdPartyVerification?.preverifiedTool?.toolType === TOOL_TYPES.INTEGRATED) ||
                thirdPartyVerification?.isCreatedByApiFullyIntegratedPreverifiedTool) && (
                <>
                  <tr>
                    <td>
                      {thirdPartyVerification.isCreatedByApiFullyIntegratedPreverifiedTool
                        ? 'Tool name and version'
                        : 'Fully pre-verified EPD tool'}
                    </td>
                    <td>{`${thirdPartyVerification?.preverifiedTool?.name || NOT_ASSIGNED}, version ${
                      thirdPartyVerification?.preverifiedToolVersion?.name || NOT_ASSIGNED
                    }`}</td>
                  </tr>
                  <tr>
                    <td>Third-party verifier, accountable for the tool and EPD verification</td>
                    <td>{`${thirdPartyVerification?.preverifiedToolVersion?.verifierName || NOT_ASSIGNED}, ${
                      thirdPartyVerification?.preverifiedToolVersion?.verifierCompanyName || NOT_ASSIGNED
                    }`}</td>
                  </tr>
                  <tr>
                    <td>Approved by</td>
                    <td>The International EPD® System</td>
                  </tr>
                </>
              )}
              <tr>
                <td>Procedure for follow-up of data during EPD validity involves third party verifier</td>
                <td>
                  <CheckboxesCellPdf>
                    <CheckboxGroupPdf label="Yes" checked={isFollowUpInvolvesThirdPartyVerifierYes} />
                    <CheckboxGroupPdf
                      label="No"
                      checked={
                        !isFollowUpInvolvesThirdPartyVerifierYes &&
                        thirdPartyVerification?.followUpInvolvesThirdPartyVerifier === 'No'
                      }
                    />
                  </CheckboxesCellPdf>
                </td>
              </tr>
              <tr>
                <td colSpan={2} style={{ backgroundColor: 'rgba(240, 240, 240, 1)', color: 'rgba(51, 51, 51, 1)' }}>
                  *EPD Process Certification involves an accredited certification body certifying and periodically auditing
                  the EPD process and conducting external and independent verification of EPDs that are regularly published.
                  More information can be found in the General Programme Instructions on www.environdec.com.
                </td>
              </tr>
            </tbody>
          </PdfTable>
        </PdfAreaDontBreak>
      </PdfPageGaps>
    </PdfPage>
  );
};

const PdfPageGaps = styled(PdfGridsArea)`
  gap: 3rem;
`;

const PdfH1Version = styled(PdfH1)<{ $isVersionTwo?: boolean }>`
  text-transform: ${(props) => (props.$isVersionTwo ? 'none' : 'uppercase')};
`;

export default ProgrammeInformationPage;
